import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Pagination, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery } from '@apollo/client';
import { QUERY_TMS_TRANSPORT_DETAIL_LIST } from '../../../../graphql/queries/dashboard/queryTmsTransportDetailList';
import type { TmsTransportDetailItem, TmsTransportDetailPage } from '../../../../graphql/types/dashboard/TmsTransportDashboardTypes';
import { formatNumberWithCommas } from '../../../../utils/numberUtils';

type ProcessedDetailItem = TmsTransportDetailItem & { _isChild?: boolean };

function reorderDetailItems(items: TmsTransportDetailItem[]): ProcessedDetailItem[] {
    const laxstemuRows: TmsTransportDetailItem[] = [];
    const others: ProcessedDetailItem[] = [];

    for (const item of items) {
        if (item.taskNo.includes('LAXSTEMU-TX')) {
            laxstemuRows.push(item);
        } else {
            others.push({ ...item });
        }
    }

    const insertedTaskNos = new Set<string>();
    for (const laxItem of laxstemuRows) {
        const date = laxItem.taskNo.substring(0, 8);
        const parentIdx = others.findIndex(
            item => item.taskNo.includes('LA-DFW-HOU') && item.taskType === '1' && item.taskNo.substring(0, 8) === date,
        );
        if (parentIdx !== -1) {
            let insertIdx = parentIdx + 1;
            while (insertIdx < others.length && others[insertIdx]._isChild) {
                insertIdx++;
            }
            others.splice(insertIdx, 0, { ...laxItem, _isChild: true });
            insertedTaskNos.add(laxItem.taskNo);
        }
    }

    for (const laxItem of laxstemuRows) {
        if (!insertedTaskNos.has(laxItem.taskNo)) {
            others.push({ ...laxItem });
        }
    }

    return others;
}

const { Text } = Typography;

const TASK_TYPE_LABELS: Record<string, string> = {
    '1': '常规',
    'REGULAR': '常规',
    '2': '临时',
    'TEMPORARY': '临时',
    '3': '三方任务',
    'THIRD_PARTY_TASK': '三方任务',
};

export interface TransportDetailContext {
    routeType: number;
    startSortingId: number;
    routeId: number;
    routeIds?: number[];   // 同名路线的全部 ID，下钻时传给后端
    startDate: string;
    endDate: string;
    metricLabel: string;
}

interface TransportDetailDrawerProps {
    open: boolean;
    context: TransportDetailContext | null;
    onClose: () => void;
}

const PAGE_SIZE = 20;

const TransportDetailDrawer: React.FC<TransportDetailDrawerProps> = ({ open, context, onClose }) => {
    const [page, setPage] = useState(1);

    const [fetchDetail, { data, loading }] = useLazyQuery<{
        queryTmsTransportDetailList: TmsTransportDetailPage;
    }>(QUERY_TMS_TRANSPORT_DETAIL_LIST);

    useEffect(() => {
        if (open && context) {
            setPage(1);
            fetchDetail({
                variables: {
                    input: {
                        routeType: context.routeType,
                        startSortingId: context.startSortingId,
                        routeId: context.routeId,
                                routeIds: context.routeIds,
                        startDate: context.startDate,
                        endDate: context.endDate,
                        page: 1,
                        size: PAGE_SIZE,
                    },
                },
            });
        }
    }, [open, context]);

    const handlePageChange = (newPage: number) => {
        if (!context) return;
        setPage(newPage);
        fetchDetail({
            variables: {
                input: {
                    routeType: context.routeType,
                    startSortingId: context.startSortingId,
                    routeId: context.routeId,
                    routeIds: context.routeIds,
                    startDate: context.startDate,
                    endDate: context.endDate,
                    page: newPage,
                    size: PAGE_SIZE,
                },
            },
        });
    };

    const pageData = data?.queryTmsTransportDetailList;

    const displayItems = useMemo(
        () => reorderDetailItems(pageData?.content ?? []),
        [pageData],
    );

    const columns: ColumnsType<ProcessedDetailItem> = [
        {
            title: '任务单号',
            dataIndex: 'taskNo',
            key: 'taskNo',
            width: 260,
            fixed: 'left',
            onCell: (record: ProcessedDetailItem) => ({
                style: record._isChild ? { backgroundColor: '#f5f5f5' } : undefined,
            }),
            render: (v: string, record: ProcessedDetailItem) =>
                record._isChild
                    ? <span style={{ paddingLeft: 16, whiteSpace: 'nowrap' }}>{v}</span>
                    : <span style={{ whiteSpace: 'nowrap' }}>{v}</span>,
        },
        { title: '承运商', dataIndex: 'carrierName', key: 'carrierName', width: 130, render: (v: string | null) => v ?? '-' },
        { title: '车牌', dataIndex: 'vehicleName', key: 'vehicleName', width: 120, render: (v: string | null) => v ?? '-' },
        { title: '车型', dataIndex: 'vehicleType', key: 'vehicleType', width: 100 },
        { title: '任务类型', dataIndex: 'taskType', key: 'taskType', width: 100, render: (v: string) => TASK_TYPE_LABELS[v] ?? v },
        {
            title: '发车包裹量',
            dataIndex: 'departPackageQty',
            key: 'departPackageQty',
            width: 110,
            render: (v: number) => formatNumberWithCommas(v),
        },
        {
            title: '发车容器数量',
            dataIndex: 'departContainerQty',
            key: 'departContainerQty',
            width: 120,
            render: (v: number | null) => v != null ? formatNumberWithCommas(v) : '-',
        },
        {
            title: '加权包裹量',
            dataIndex: 'weightedPackageQty',
            key: 'weightedPackageQty',
            width: 110,
            render: (v: number) => formatNumberWithCommas(v),
        },
        {
            title: <span style={{ whiteSpace: 'nowrap' }}>满载包裹上限</span>,
            dataIndex: 'fullLoadPackageMaxLimit',
            key: 'fullLoadPackageMaxLimit',
            width: 120,
            render: (v: number | null) => v != null ? formatNumberWithCommas(v) : '-',
        },
        {
            title: <span style={{ whiteSpace: 'nowrap' }}>满载重量上限（千克）</span>,
            dataIndex: 'fullLoadWeightMaxLimit',
            key: 'fullLoadWeightMaxLimit',
            width: 175,
            render: (v: number | null) => v != null ? formatNumberWithCommas(v) : '-',
        },
        { title: '起点计划发车（美西）', dataIndex: 'planOriginDepartTime', key: 'planOriginDepartTime', width: 185, render: (v: string | null) => v ?? '-' },
        { title: '起点实际发车（美西）', dataIndex: 'actualOriginDepartTime', key: 'actualOriginDepartTime', width: 185, render: (v: string | null) => v ?? '-' },
        { title: '终点预计到达（美西）', dataIndex: 'estimateDestArrivalTime', key: 'estimateDestArrivalTime', width: 185, render: (v: string | null) => v ?? '-' },
        { title: '终点计划到达（美西）', dataIndex: 'planDestArrivalTime', key: 'planDestArrivalTime', width: 185, render: (v: string | null) => v ?? '-' },
        { title: '终点实际到达（美西）', dataIndex: 'actualDestArrivalTime', key: 'actualDestArrivalTime', width: 185, render: (v: string | null) => v ?? '-' },
        {
            title: '理论行驶时长(h)',
            dataIndex: 'theoryDriveDuration',
            key: 'theoryDriveDuration',
            width: 140,
            render: (v: string) => formatNumberWithCommas(parseFloat(v), 1),
        },
        {
            title: '实际行驶时长(h)',
            dataIndex: 'actualDriveDuration',
            key: 'actualDriveDuration',
            width: 140,
            render: (v: string) => formatNumberWithCommas(parseFloat(v), 1),
        },
        {
            title: '单车成本',
            dataIndex: 'singleVehicleCost',
            key: 'singleVehicleCost',
            width: 110,
            render: (v: string) => formatNumberWithCommas(parseFloat(v), 2),
        },
    ];

    const title = context
        ? `运输明细（${context.startDate}${context.startDate !== context.endDate ? ` ～ ${context.endDate}` : ''}）`
        : '运输明细';

    return (
        <Drawer
            title={title}
            open={open}
            onClose={onClose}
            width={1400}
            destroyOnClose
        >
            <Table<ProcessedDetailItem>
                columns={columns}
                dataSource={displayItems}
                loading={loading}
                rowKey="taskNo"
                scroll={{ x: 2200 }}
                pagination={false}
                size="small"
                onRow={(record) => ({
                    style: record._isChild ? { backgroundColor: '#f5f5f5' } : undefined,
                })}
            />
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                    current={page}
                    pageSize={PAGE_SIZE}
                    total={Number(pageData?.totalElements) || 0}
                    showTotal={(total) => `共 ${formatNumberWithCommas(total)} 条`}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                />
            </div>
        </Drawer>
    );
};

export default TransportDetailDrawer;
